// 🎨 Enhanced 90s Pixel Art Sprite Factory
// Creates detailed Mario-style 16x16 pixel sprites with better textures

import { GAME_CONFIG } from '../game/config.js';

export class SpriteFactory {
  constructor(scene) {
    this.scene = scene;
    this.colors = GAME_CONFIG.SPRITES.COLORS;
    this.tileSize = GAME_CONFIG.SPRITES.TILE_SIZE;
  }

  // Create all game sprites with enhanced textures
  createAllSprites() {
   /*  console.log('🎨 Creating enhanced 90s pixel art sprites...'); */
    
    // Character sprites
    this.createBrideSprite();
    this.createGroomSprite();
    this.createCatSprites();
    this.createMilesSprite();
    
    // NEW: Create NPC variants
    this.createNPCVariants();
    
    // Environment sprites with textures
    this.createTexturedPlatformSprites();
    this.createEnhancedBackgroundSprites();
    
    // Item sprites
    this.createItemSprites();
    
    // Enhanced obstacle sprites
    this.createRealisticObstacleSprites();
    
    // UI sprites
    this.createUISprites();
    
    console.log('✅ All enhanced sprites created!');
  }

  createNPCVariants() {
    // Salesperson with blue shirt and tie
    this.createPixelSprite('salesperson-blue', 28, 40, (g) => {
      // Skin color
      g.fillStyle(0xFFDBB5);
      g.fillRect(8, 4, 12, 12); // Head
      g.fillRect(4, 24, 4, 6); // Left arm
      g.fillRect(20, 24, 4, 6); // Right arm
      
      // Professional hair (slicked back)
      g.fillStyle(0x2F1B14);
      g.fillRect(6, 2, 16, 6); // Neat hair
      g.fillRect(8, 1, 12, 3); // Hair top
      
      // Blue business shirt
      g.fillStyle(0x4169E1);
      g.fillRect(8, 16, 12, 16); // Shirt
      g.fillRect(4, 20, 20, 12); // Shirt spread
      
      // White collar
      g.fillStyle(0xFFFFFF);
      g.fillRect(10, 16, 8, 3); // Collar
      
      // Red tie
      g.fillStyle(0xDC143C);
      g.fillRect(12, 19, 4, 10); // Tie
      
      // Dark pants
      g.fillStyle(0x2F2F2F);
      g.fillRect(10, 32, 8, 8); // Pants
      
      // Face features
      g.fillStyle(0x000000);
      g.fillRect(10, 8, 2, 2); // Left eye
      g.fillRect(16, 8, 2, 2); // Right eye
      g.fillStyle(0xFF69B4);
      g.fillRect(12, 12, 4, 2); // Smile
      
      // Glasses (professional look)
      g.fillStyle(0x2F2F2F);
      g.fillRect(9, 7, 5, 1); // Left frame
      g.fillRect(14, 7, 5, 1); // Right frame
      g.fillRect(14, 8, 1, 3); // Bridge
      
      // Name tag
      g.fillStyle(0xFFFFFF);
      g.fillRect(6, 22, 6, 3); // Name tag
      g.fillStyle(0x000000);
      g.fillRect(7, 23, 4, 1); // Text
      
      // Shoes
      g.fillStyle(0x000000);
      g.fillRect(8, 38, 4, 2); // Left shoe
      g.fillRect(16, 38, 4, 2); // Right shoe
    });

    // Salesperson with red shirt (different look)
    this.createPixelSprite('salesperson-red', 28, 40, (g) => {
      // Skin color
      g.fillStyle(0xFFDBB5);
      g.fillRect(8, 4, 12, 12); // Head
      g.fillRect(4, 24, 4, 6); // Left arm
      g.fillRect(20, 24, 4, 6); // Right arm
      
      // Different hair (curly)
      g.fillStyle(0x8B4513);
      g.fillRect(6, 2, 16, 6); // Curly hair
      g.fillRect(5, 3, 4, 4); // Left curl
      g.fillRect(19, 3, 4, 4); // Right curl
      
      // Red polo shirt
      g.fillStyle(0xDC143C);
      g.fillRect(8, 16, 12, 16); // Shirt
      g.fillRect(4, 20, 20, 12); // Shirt spread
      
      // White collar (polo style)
      g.fillStyle(0xFFFFFF);
      g.fillRect(11, 16, 6, 2); // Polo collar
      
      // Khaki pants
      g.fillStyle(0xF0E68C);
      g.fillRect(10, 32, 8, 8); // Khaki pants
      
      // Face features (different expression)
      g.fillStyle(0x000000);
      g.fillRect(10, 8, 2, 2); // Left eye
      g.fillRect(16, 8, 2, 2); // Right eye
      g.fillStyle(0xFF69B4);
      g.fillRect(11, 12, 6, 2); // Wide smile
      
      // No glasses (different look)
      
      // Store badge
      g.fillStyle(0xFFD700);
      g.fillRect(18, 22, 4, 4); // Gold badge
      g.fillStyle(0x000000);
      g.fillRect(19, 23, 2, 2); // Badge symbol
      
      // Sneakers (casual)
      g.fillStyle(0xFFFFFF);
      g.fillRect(8, 38, 4, 2); // Left sneaker
      g.fillRect(16, 38, 4, 2); // Right sneaker
    });

    // Waiter with white uniform
    this.createPixelSprite('waiter-white', 28, 40, (g) => {
      // Skin color
      g.fillStyle(0xFFDBB5);
      g.fillRect(8, 4, 12, 12); // Head
      g.fillRect(4, 24, 4, 6); // Left arm
      g.fillRect(20, 24, 4, 6); // Right arm
      
      // Professional waiter hair
      g.fillStyle(0x2F1B14);
      g.fillRect(7, 2, 14, 5); // Neat hair
      
      // White waiter shirt
      g.fillStyle(0xFFFFFF);
      g.fillRect(8, 16, 12, 16); // White shirt
      g.fillRect(4, 20, 20, 12); // Shirt spread
      
      // Black bow tie
      g.fillStyle(0x000000);
      g.fillRect(11, 18, 6, 3); // Bow tie
      g.fillRect(13, 19, 2, 1); // Bow center
      
      // Black vest
      g.fillStyle(0x2F2F2F);
      g.fillRect(9, 21, 10, 8); // Vest
      
      // Black formal pants
      g.fillStyle(0x000000);
      g.fillRect(10, 32, 8, 8); // Black pants
      
      // Face features (professional)
      g.fillStyle(0x000000);
      g.fillRect(10, 8, 2, 2); // Left eye
      g.fillRect(16, 8, 2, 2); // Right eye
      g.fillStyle(0xFF69B4);
      g.fillRect(12, 12, 4, 1); // Professional smile
      
      // Mustache (waiter style)
      g.fillStyle(0x2F1B14);
      g.fillRect(11, 11, 6, 1); // Mustache
      
      // White apron
      g.fillStyle(0xF8F8FF);
      g.fillRect(6, 26, 16, 6); // Apron
      g.fillStyle(0xE0E0E0);
      g.fillRect(6, 26, 16, 1); // Apron edge
      
      // Formal shoes
      g.fillStyle(0x000000);
      g.fillRect(8, 38, 4, 2); // Left shoe
      g.fillRect(16, 38, 4, 2); // Right shoe
      
      // Carrying tray
      g.fillStyle(0xC0C0C0);
      g.fillRect(22, 20, 6, 1); // Tray
      g.fillStyle(0xFFFFFF);
      g.fillRect(23, 19, 2, 1); // Glass on tray
      g.fillRect(25, 19, 2, 1); // Another glass
    });

    // Waiter with black uniform (different style)
    this.createPixelSprite('waiter-black', 28, 40, (g) => {
      // Skin color
      g.fillStyle(0xFFDBB5);
      g.fillRect(8, 4, 12, 12); // Head
      g.fillRect(4, 24, 4, 6); // Left arm
      g.fillRect(20, 24, 4, 6); // Right arm
      
      // Different hair (feminine/longer)
      g.fillStyle(0x654321);
      g.fillRect(6, 2, 16, 6); // Hair
      g.fillRect(4, 4, 4, 8); // Left side hair
      g.fillRect(20, 4, 4, 8); // Right side hair
      
      // Black waiter shirt
      g.fillStyle(0x2F2F2F);
      g.fillRect(8, 16, 12, 16); // Black shirt
      g.fillRect(4, 20, 20, 12); // Shirt spread
      
      // White collar
      g.fillStyle(0xFFFFFF);
      g.fillRect(10, 16, 8, 2); // White collar
      
      // White bow tie
      g.fillStyle(0xFFFFFF);
      g.fillRect(11, 18, 6, 3); // White bow tie
      g.fillStyle(0xE0E0E0);
      g.fillRect(13, 19, 2, 1); // Bow center
      
      // Black skirt (female waiter)
      g.fillStyle(0x000000);
      g.fillRect(9, 32, 10, 8); // Black skirt
      
      // Face features (different expression)
      g.fillStyle(0x000000);
      g.fillRect(10, 8, 2, 2); // Left eye
      g.fillRect(16, 8, 2, 2); // Right eye
      g.fillStyle(0xFF69B4);
      g.fillRect(12, 12, 4, 2); // Friendly smile
      
      // Earrings
      g.fillStyle(0xFFD700);
      g.fillRect(8, 9, 1, 1); // Left earring
      g.fillRect(19, 9, 1, 1); // Right earring
      
      // White apron
      g.fillStyle(0xFFFEF7);
      g.fillRect(7, 26, 14, 6); // Apron
      g.fillStyle(0xF0F0F0);
      g.fillRect(7, 26, 14, 1); // Apron edge
      
      // Formal heels
      g.fillStyle(0x000000);
      g.fillRect(9, 38, 3, 2); // Left heel
      g.fillRect(16, 38, 3, 2); // Right heel
      
      // Carrying notepad
      g.fillStyle(0xFFFFFF);
      g.fillRect(2, 22, 4, 3); // Notepad
      g.fillStyle(0x000000);
      g.fillRect(2, 22, 4, 1); // Notepad lines
    });

    console.log('✅ NPC variant sprites created');
  }

  // Helper function to create pixel art
  createPixelSprite(key, width, height, drawFunction) {
    const graphics = this.scene.add.graphics();
    drawFunction(graphics);
    graphics.generateTexture(key, width, height);
    graphics.destroy();
  }

  // === ENHANCED CHARACTER SPRITES ===
  
  createBrideSprite() {
    // Bettina (Bride) - 32x32 high resolution sprite with long brown hair
    this.createPixelSprite('bride', 32, 32, (g) => {
      // Skin tone (realistic)
      g.fillStyle(0xFFDBB5);
      g.fillRect(10, 8, 12, 10); // Face
      g.fillRect(8, 16, 4, 6); // Left arm
      g.fillRect(20, 16, 4, 6); // Right arm
      g.fillRect(12, 18, 8, 4); // Neck/chest
      
      // Long brown hair (flowing, shoulder length)
      g.fillStyle(0x8B4513);
      g.fillRect(7, 6, 18, 6); // Hair base
      g.fillRect(5, 8, 6, 12); // Left side long hair
      g.fillRect(21, 8, 6, 12); // Right side long hair
      g.fillRect(6, 10, 4, 10); // Hair flow left
      g.fillRect(22, 10, 4, 10); // Hair flow right
      g.fillRect(4, 12, 3, 8); // Extra long hair left
      g.fillRect(25, 12, 3, 8); // Extra long hair right
      
      // Hair highlights (lighter brown)
      g.fillStyle(0xA0522D);
      g.fillRect(9, 6, 3, 2);
      g.fillRect(20, 6, 3, 2);
      g.fillRect(6, 10, 2, 4);
      g.fillRect(24, 10, 2, 4);
      
      // Wedding dress (elegant white)
      g.fillStyle(0xFFFEF7);
      g.fillRect(6, 22, 20, 10); // Dress body
      g.fillRect(8, 20, 16, 6); // Dress top
      g.fillRect(4, 24, 24, 8); // Full skirt
      
      // Dress details
      g.fillStyle(0xF0F0F0); // Dress shading
      g.fillRect(23, 22, 3, 10); // Right shadow
      g.fillRect(4, 30, 24, 2); // Bottom shadow
      
      // Dress embellishments
      g.fillStyle(0xE6E6FA);
      g.fillRect(10, 21, 12, 1); // Neckline
      g.fillRect(8, 24, 16, 1); // Waist detail
      g.fillRect(12, 26, 8, 1); // Skirt detail
      
      // Detailed face features
      // Eyes (larger, more expressive)
      g.fillStyle(0x4169E1); // Blue eyes
      g.fillRect(12, 11, 2, 2);
      g.fillRect(18, 11, 2, 2);
      
      // Eye details
      g.fillStyle(0x000000); // Pupils
      g.fillRect(12, 11, 1, 1);
      g.fillRect(18, 11, 1, 1);
      g.fillStyle(0xFFFFFF); // Eye shine
      g.fillRect(13, 11, 1, 1);
      g.fillRect(19, 11, 1, 1);
      
      // Eyebrows
      g.fillStyle(0x654321);
      g.fillRect(11, 9, 3, 1);
      g.fillRect(18, 9, 3, 1);
      
      // Nose (subtle)
      g.fillStyle(0xF4C2A1);
      g.fillRect(15, 13, 2, 2);
      
      // Mouth (happy smile)
      g.fillStyle(0xFF69B4);
      g.fillRect(14, 15, 4, 1);
      g.fillRect(13, 16, 6, 1);
      
      // Cheeks (rosy)
      g.fillStyle(0xFFB6C1);
      g.fillRect(10, 13, 2, 2);
      g.fillRect(20, 13, 2, 2);
      
      // Wedding veil (flowing behind hair)
      g.fillStyle(0xF8F8FF);
      g.fillRect(3, 8, 4, 12); // Left veil
      g.fillRect(25, 8, 4, 12); // Right veil
      g.fillRect(2, 10, 3, 10); // Veil flow left
      g.fillRect(27, 10, 3, 10); // Veil flow right
      
      // Veil details
      g.fillStyle(0xE6E6FA);
      g.fillRect(4, 12, 2, 6);
      g.fillRect(26, 12, 2, 6);
      
      // Bridal bouquet (detailed)
      g.fillStyle(0xFF1493); // Pink roses
      g.fillRect(2, 18, 4, 4);
      g.fillRect(3, 20, 4, 4);
      g.fillStyle(0xFFFFFF); // White flowers
      g.fillRect(1, 19, 2, 2);
      g.fillRect(5, 21, 2, 2);
      g.fillStyle(0xFFD700); // Yellow centers
      g.fillRect(2, 19, 1, 1);
      g.fillRect(4, 21, 1, 1);
      g.fillStyle(0x228B22); // Green stems
      g.fillRect(3, 22, 2, 6);
      g.fillStyle(0x32CD32); // Leaves
      g.fillRect(1, 23, 2, 2);
      g.fillRect(5, 24, 2, 2);
      
      // Jewelry (wedding ring visible)
      g.fillStyle(0xFFD700);
      g.fillRect(21, 19, 1, 1);
      
      // Shoes (elegant heels)
      g.fillStyle(0xFFFFFF);
      g.fillRect(10, 30, 3, 2);
      g.fillRect(19, 30, 3, 2);
      g.fillStyle(0xC0C0C0);
      g.fillRect(10, 31, 3, 1);
      g.fillRect(19, 31, 3, 1);
    });
  }

  createGroomSprite() {
    // Pablo (Groom) - 32x32 high resolution sprite with long black hair, no hat
    this.createPixelSprite('groom', 32, 32, (g) => {
      // Skin tone (realistic)
      g.fillStyle(0xFFDBB5);
      g.fillRect(10, 9, 12, 9); // Face
      g.fillRect(8, 17, 4, 6); // Left arm
      g.fillRect(20, 17, 4, 6); // Right arm
      g.fillRect(12, 18, 8, 4); // Neck/chest
      
      // Long black hair (no hat, flowing style)
      g.fillStyle(0x2F1B14);
      g.fillRect(7, 5, 18, 6); // Hair base/top
      g.fillRect(5, 7, 6, 10); // Left side long hair
      g.fillRect(21, 7, 6, 10); // Right side long hair
      g.fillRect(6, 9, 4, 8); // Hair flow left
      g.fillRect(22, 9, 4, 8); // Hair flow right
      g.fillRect(4, 11, 3, 6); // Extra long hair left
      g.fillRect(25, 11, 3, 6); // Extra long hair right
      
      // Hair styling/texture
      g.fillStyle(0x4A2C2A);
      g.fillRect(9, 5, 3, 2);
      g.fillRect(20, 5, 3, 2);
      g.fillRect(6, 9, 2, 3);
      g.fillRect(24, 9, 2, 3);
      
      // Facial hair (beard/mustache)
      g.fillStyle(0x2F1B14);
      g.fillRect(13, 16, 6, 1); // Mustache
      g.fillRect(12, 17, 8, 1); // Beard line
      g.fillRect(13, 18, 6, 1); // Beard bottom
      
      // Black formal suit (high quality)
      g.fillStyle(0x1C1C1C);
      g.fillRect(8, 22, 16, 10); // Suit body
      g.fillRect(6, 24, 20, 8); // Suit jacket
      
      // Suit details and shading
      g.fillStyle(0x0F0F0F);
      g.fillRect(22, 24, 4, 8); // Right shadow
      g.fillRect(6, 30, 20, 2); // Bottom shadow
      
      // White dress shirt (crisp)
      g.fillStyle(0xFFFEF7);
      g.fillRect(12, 23, 8, 8); // Shirt front
      g.fillStyle(0xF0F0F0);
      g.fillRect(18, 23, 2, 8); // Shirt fold
      
      // Suit buttons (detailed)
      g.fillStyle(0xC0C0C0);
      g.fillRect(14, 25, 1, 1);
      g.fillRect(14, 27, 1, 1);
      g.fillRect(14, 29, 1, 1);
      
      // Red tie (elegant)
      g.fillStyle(0xDC143C);
      g.fillRect(15, 22, 2, 2); // Tie knot
      g.fillRect(15, 24, 2, 6); // Tie body
      g.fillStyle(0xB22222);
      g.fillRect(16, 22, 1, 8); // Tie center line
      
      // Suit lapels (detailed)
      g.fillStyle(0x2F2F2F);
      g.fillRect(10, 23, 2, 5);
      g.fillRect(20, 23, 2, 5);
      
      // Detailed face features
      // Eyes (warm, friendly)
      g.fillStyle(0x654321); // Brown eyes
      g.fillRect(12, 12, 2, 2);
      g.fillRect(18, 12, 2, 2);
      
      // Eye details
      g.fillStyle(0x000000); // Pupils
      g.fillRect(12, 12, 1, 1);
      g.fillRect(18, 12, 1, 1);
      g.fillStyle(0xFFFFFF); // Eye shine
      g.fillRect(13, 12, 1, 1);
      g.fillRect(19, 12, 1, 1);
      
      // Glasses (stylish frames)
      g.fillStyle(0x2F2F2F);
      g.fillRect(11, 11, 4, 1); // Left frame top
      g.fillRect(17, 11, 4, 1); // Right frame top
      g.fillRect(11, 14, 4, 1); // Left frame bottom
      g.fillRect(17, 14, 4, 1); // Right frame bottom
      g.fillRect(11, 11, 1, 4); // Left frame side
      g.fillRect(14, 11, 1, 4); // Left frame side
      g.fillRect(17, 11, 1, 4); // Right frame side
      g.fillRect(20, 11, 1, 4); // Right frame side
      g.fillRect(15, 12, 2, 1); // Bridge
      
      // Eyebrows (under glasses)
      g.fillStyle(0x2F1B14);
      g.fillRect(11, 10, 3, 1);
      g.fillRect(18, 10, 3, 1);
      
      // Nose
      g.fillStyle(0xF4C2A1);
      g.fillRect(15, 14, 2, 2);
      
      // Mouth (confident smile under mustache)
      g.fillStyle(0xCD853F);
      g.fillRect(14, 18, 4, 1);
      
      // Boutonniere (elegant detail)
      g.fillStyle(0xFF69B4);
      g.fillRect(7, 24, 2, 2); // Pink flower
      g.fillStyle(0x228B22);
      g.fillRect(7, 26, 1, 2); // Green stem
      g.fillStyle(0x32CD32);
      g.fillRect(6, 24, 1, 1); // Leaf
      g.fillStyle(0xFFD700); // Flower center
      g.fillRect(7, 24, 1, 1);
      
      // Cufflinks (luxury detail)
      g.fillStyle(0xFFD700);
      g.fillRect(8, 20, 1, 1);
      g.fillRect(23, 20, 1, 1);
      
      // Formal shoes (polished black)
      g.fillStyle(0x1C1C1C);
      g.fillRect(10, 30, 3, 2);
      g.fillRect(19, 30, 3, 2);
      g.fillStyle(0x696969); // Shoe shine
      g.fillRect(10, 30, 1, 1);
      g.fillRect(19, 30, 1, 1);
      g.fillStyle(0x000000); // Shoe soles
      g.fillRect(10, 31, 3, 1);
      g.fillRect(19, 31, 3, 1);
    });
  }

  // === UPDATED CAT SPRITES (32x24) ===
  
  createCatSprites() {
    // Enhanced Nova (Orange cat) - 32x24 sprite (bigger and more detailed)
    this.createPixelSprite('nova-cat', 32, 24, (g) => {
      // Orange fur base
      g.fillStyle(0xFF8C00);
      g.fillRect(4, 8, 24, 12); // Body
      g.fillRect(8, 4, 16, 8); // Head
      
      // Orange stripes
      g.fillStyle(0xDC143C);
      g.fillRect(8, 6, 16, 2); // Head stripe
      g.fillRect(6, 10, 20, 2); // Body stripe 1
      g.fillRect(6, 14, 20, 2); // Body stripe 2
      
      // Cat features
      g.fillStyle(0x000000);
      g.fillRect(12, 6, 2, 2); // Left eye
      g.fillRect(18, 6, 2, 2); // Right eye
      g.fillRect(4, 2, 4, 4); // Left ear
      g.fillRect(24, 2, 4, 4); // Right ear
      g.fillRect(28, 10, 4, 2); // Tail start
      
      // Inner ears
      g.fillStyle(0xFF69B4);
      g.fillRect(5, 3, 2, 2); // Left inner ear
      g.fillRect(25, 3, 2, 2); // Right inner ear
      
      // Pink nose and mouth
      g.fillStyle(0xFF69B4);
      g.fillRect(14, 8, 4, 2); // Nose
      g.fillStyle(0x000000);
      g.fillRect(14, 10, 2, 2); // Mouth left
      g.fillRect(16, 10, 2, 2); // Mouth right
      
      // Whiskers
      g.fillStyle(0x000000);
      g.fillRect(8, 8, 4, 1); // Left whiskers
      g.fillRect(20, 8, 4, 1); // Right whiskers
      
      // Paws
      g.fillStyle(0xFF69B4);
      g.fillRect(8, 18, 4, 2); // Front left paw
      g.fillRect(20, 18, 4, 2); // Front right paw
    });

    // Enhanced Linus (Gray cat) - 32x24 sprite
    this.createPixelSprite('linus-cat', 32, 24, (g) => {
      // Gray fur base
      g.fillStyle(0x808080);
      g.fillRect(4, 8, 24, 12); // Body
      g.fillRect(8, 4, 16, 8); // Head
      
      // Gray stripes (darker)
      g.fillStyle(0x404040);
      g.fillRect(8, 6, 16, 2); // Head stripe
      g.fillRect(6, 10, 20, 2); // Body stripe 1
      g.fillRect(6, 14, 20, 2); // Body stripe 2
      
      // Cat features
      g.fillStyle(0x000000);
      g.fillRect(12, 6, 2, 2); // Left eye
      g.fillRect(18, 6, 2, 2); // Right eye
      g.fillRect(4, 2, 4, 4); // Left ear
      g.fillRect(24, 2, 4, 4); // Right ear
      g.fillRect(28, 10, 4, 2); // Tail start
      
      // Inner ears
      g.fillStyle(0xFF69B4);
      g.fillRect(5, 3, 2, 2); // Left inner ear
      g.fillRect(25, 3, 2, 2); // Right inner ear
      
      // Pink nose and mouth
      g.fillStyle(0xFF69B4);
      g.fillRect(14, 8, 4, 2); // Nose
      g.fillStyle(0x000000);
      g.fillRect(14, 10, 2, 2); // Mouth left
      g.fillRect(16, 10, 2, 2); // Mouth right
      
      // Whiskers
      g.fillStyle(0x000000);
      g.fillRect(8, 8, 4, 1); // Left whiskers
      g.fillRect(20, 8, 4, 1); // Right whiskers
      
      // Paws
      g.fillStyle(0xFF69B4);
      g.fillRect(8, 18, 4, 2); // Front left paw
      g.fillRect(20, 18, 4, 2); // Front right paw
    });
  }

  // === UPDATED MILES SPRITE (28x40) ===
  
  createMilesSprite() {
    // Enhanced Miles (7-year-old) - 28x40 sprite (bigger and more detailed)
    this.createPixelSprite('miles', 28, 40, (g) => {
      // Skin color
      g.fillStyle(0xFFDBB5);
      g.fillRect(8, 4, 12, 12); // Head
      g.fillRect(4, 24, 4, 6); // Left arm
      g.fillRect(20, 24, 4, 6); // Right arm
      
      // Kid hair (messy brown)
      g.fillStyle(0x8B4513);
      g.fillRect(6, 2, 16, 6); // Hair base
      g.fillRect(4, 4, 4, 4); // Messy left side
      g.fillRect(20, 4, 4, 4); // Messy right side
      g.fillStyle(0xA0522D); // Hair highlights
      g.fillRect(8, 2, 4, 2);
      g.fillRect(16, 2, 4, 2);
      
      // Blue shirt (kid-sized) with texture
      g.fillStyle(0x4169E1);
      g.fillRect(8, 16, 12, 16); // Shirt base
      g.fillRect(4, 20, 20, 12); // Shirt spread
      g.fillStyle(0x1E5AAA); // Shirt shading
      g.fillRect(18, 20, 6, 12); // Right side shadow
      g.fillRect(4, 28, 20, 4); // Bottom shadow
      
      // Shirt collar and details
      g.fillStyle(0x87CEEB); // Light blue
      g.fillRect(12, 16, 4, 2); // Collar
      g.fillRect(10, 20, 8, 2); // Shirt stripe
      
      // Khaki shorts with texture
      g.fillStyle(0xF0E68C);
      g.fillRect(10, 32, 8, 8); // Shorts
      g.fillStyle(0xDAA520); // Shorts shading
      g.fillRect(16, 32, 2, 8); // Right side shadow
      
      // Enhanced face features
      g.fillStyle(0x000000);
      g.fillRect(10, 8, 2, 2); // Left eye
      g.fillRect(16, 8, 2, 2); // Right eye
      g.fillStyle(0xFFB6C1); // Rosy cheeks
      g.fillRect(8, 10, 2, 2); // Left cheek
      g.fillRect(18, 10, 2, 2); // Right cheek
      g.fillStyle(0xFF69B4);
      g.fillRect(12, 12, 4, 2); // Big kid smile
      
      // Freckles
      g.fillStyle(0xDEB887);
      g.fillRect(10, 10, 1, 1); // Left freckle
      g.fillRect(16, 10, 1, 1); // Right freckle
      
      // Shoes
      g.fillStyle(0x8B4513);
      g.fillRect(8, 38, 4, 2); // Left shoe
      g.fillRect(16, 38, 4, 2); // Right shoe
      
      // Ring pillow (enhanced)
      g.fillStyle(0xFFFEF7);
      g.fillRect(2, 22, 8, 6); // Pillow base
      g.fillStyle(0xF0F0F0); // Pillow shading
      g.fillRect(2, 26, 8, 2); // Bottom shadow
      g.fillStyle(0xFFD700);
      g.fillRect(4, 24, 4, 2); // Ring
      g.fillStyle(0xFF1493); // Pillow ribbon
      g.fillRect(4, 22, 4, 2); // Ribbon
    });
  }


  // === ENHANCED PLATFORM SPRITES WITH TEXTURES ===
  
  createTexturedPlatformSprites() {
    // Enhanced grass platform with detailed texture - 64x48
    this.createPixelSprite('grass-platform', 64, 48, (g) => {
      // Dark green grass top with texture
      g.fillStyle(0x2D5F3F);
      g.fillRect(0, 0, 64, 16);
      
      // Grass blades on top
      g.fillStyle(0x4F8B5B);
      for (let i = 0; i < 32; i++) {
        const x = i * 2;
        g.fillRect(x, 0, 2, 8 + (i % 6));
        g.fillRect(x + 1, 0, 2, 4 + (i % 4));
      }
      
      // Medium green middle layer
      g.fillStyle(0x3D7C47);
      g.fillRect(0, 16, 64, 8);
      
      // Brown dirt with texture
      g.fillStyle(0x8B4513);
      g.fillRect(0, 24, 64, 24);
      
      // Dirt texture and rocks
      g.fillStyle(0x654321);
      for (let i = 0; i < 16; i++) {
        g.fillRect((i * 4) + 2, 28 + (i % 6), 4, 2);
        g.fillRect((i * 4) + 4, 32 + (i % 4), 2, 4);
      }
      
      // Small rocks
      g.fillStyle(0x696969);
      g.fillRect(6, 30, 2, 2);
      g.fillRect(14, 34, 2, 2);
      g.fillRect(30, 28, 2, 2);
      g.fillRect(46, 32, 2, 2);
      g.fillRect(56, 30, 2, 2);
    });

    // Enhanced wooden platform (shop level) - 64x40
    this.createPixelSprite('wood-platform', 64, 40, (g) => {
      // Brown wood base
      g.fillStyle(0x8B4513);
      g.fillRect(0, 8, 64, 32);
      
      // Wood grain texture
      g.fillStyle(0xA0522D);
      g.fillRect(0, 12, 64, 4); // Horizontal grain
      g.fillRect(0, 20, 64, 2);
      g.fillRect(0, 28, 64, 4);
      g.fillRect(0, 36, 64, 2);
      
      // Vertical wood planks
      g.fillStyle(0x654321);
      for (let i = 0; i < 8; i++) {
        g.fillRect(i * 8, 8, 2, 32); // Plank separators
      }
      
      // Wood knots and details
      g.fillStyle(0x654321);
      g.fillRect(12, 16, 4, 2);
      g.fillRect(28, 24, 4, 2);
      g.fillRect(44, 18, 4, 2);
      g.fillRect(60, 26, 2, 4);
      
      // Platform edge highlight
      g.fillStyle(0xDEB887);
      g.fillRect(0, 8, 64, 2); // Top highlight
    });

    // Enhanced stone platform (ceremony level) - 64x48
    this.createPixelSprite('stone-platform', 64, 48, (g) => {
      // Gray stone base
      g.fillStyle(0x708090);
      g.fillRect(0, 8, 64, 40);
      
      // Stone texture with blocks
      g.fillStyle(0x2F4F4F);
      // Draw stone blocks
      for (let y = 0; y < 5; y++) {
        for (let x = 0; x < 8; x++) {
          g.fillRect(x * 8, 8 + (y * 8), 7, 7); // Stone block
          g.strokeRect(x * 8, 8 + (y * 8), 7, 7); // Block outline
        }
      }
      
      // Stone highlights
      g.fillStyle(0x9AC0CD);
      g.fillRect(2, 10, 60, 2); // Top highlight
      for (let i = 0; i < 8; i++) {
        g.fillRect(i * 8 + 2, 10, 2, 30); // Vertical highlights
      }
      
      // Moss on stone
      g.fillStyle(0x2F5F2F);
      g.fillRect(6, 40, 4, 2);
      g.fillRect(24, 42, 6, 2);
      g.fillRect(50, 38, 4, 4);
    });

    // Enhanced party platform (disco level) - 64x40
    this.createPixelSprite('disco-platform', 64, 40, (g) => {
      // Metallic base
      g.fillStyle(0x2C3E50);
      g.fillRect(0, 8, 64, 32);
      
      // Disco checkerboard pattern
      for (let y = 0; y < 8; y++) {
        for (let x = 0; x < 16; x++) {
          const isLight = (x + y) % 2 === 0;
          g.fillStyle(isLight ? 0x4A4A4A : 0x1A1A1A);
          g.fillRect(x * 4, 8 + (y * 4), 4, 4);
        }
      }
      
      // Metallic highlights
      g.fillStyle(0x85C1E9);
      g.fillRect(0, 8, 64, 2); // Top shine
      g.fillRect(0, 38, 64, 2); // Bottom shine
      
      // LED strip lights
      const colors = [0xFF1493, 0x00CED1, 0xFFD700, 0x32CD32];
      for (let i = 0; i < 16; i++) {
        g.fillStyle(colors[i % 4]);
        g.fillRect(i * 4 + 2, 6, 4, 2); // Top LEDs
      }
    });
  }

  // === ENHANCED BACKGROUND SPRITES ===
  
  createEnhancedBackgroundSprites() {
    // Enhanced cloud with more detail - 48x24
    this.createPixelSprite('cloud', 48, 24, (g) => {
      g.fillStyle(0xFFFFFF);
      g.fillRect(8, 8, 32, 12); // Main cloud
      g.fillRect(12, 4, 24, 8); // Cloud top
      g.fillRect(4, 10, 8, 8); // Left puff
      g.fillRect(36, 10, 8, 8); // Right puff
      g.fillRect(16, 16, 16, 6); // Bottom puff
      
      // Cloud shading
      g.fillStyle(0xF0F8FF);
      g.fillRect(10, 6, 20, 2); // Top highlight
      g.fillRect(8, 10, 28, 2); // Middle highlight
      
      // Cloud shadow
      g.fillStyle(0xE6E6FA);
      g.fillRect(8, 18, 32, 2); // Bottom shadow
    });

    // Enhanced heart decoration - 24x20
    this.createPixelSprite('heart', 24, 20, (g) => {
      g.fillStyle(0xFF1493);
      g.fillRect(4, 6, 6, 6); // Left heart
      g.fillRect(14, 6, 6, 6); // Right heart
      g.fillRect(6, 10, 12, 8); // Heart middle
      g.fillRect(10, 16, 4, 2); // Heart bottom
      
      // Heart highlights
      g.fillStyle(0xFF69B4);
      g.fillRect(4, 6, 2, 4); // Left highlight
      g.fillRect(14, 6, 2, 4); // Right highlight
      g.fillRect(10, 10, 4, 2); // Center highlight
    });

    // Enhanced wedding bell - 24x28
    this.createPixelSprite('bell', 24, 28, (g) => {
      g.fillStyle(0xFFD700);
      g.fillRect(6, 6, 12, 16); // Bell body
      g.fillRect(4, 12, 16, 6); // Bell middle
      g.fillRect(8, 2, 8, 8); // Bell top
      
      // Bell highlights
      g.fillStyle(0xFFF8DC);
      g.fillRect(6, 6, 2, 16); // Left highlight
      g.fillRect(8, 4, 8, 2); // Top highlight
      
      // Bell shadow
      g.fillStyle(0xDAA520);
      g.fillRect(16, 6, 2, 16); // Right shadow
      g.fillRect(6, 20, 12, 2); // Bottom shadow
      
      // Clapper
      g.fillStyle(0xC0C0C0);
      g.fillRect(10, 18, 4, 4); // Bell clapper
    });

    // Enhanced star - 24x24
    this.createPixelSprite('star', 24, 24, (g) => {
      g.fillStyle(0xFFD700);
      g.fillRect(10, 2, 4, 6); // Top point
      g.fillRect(4, 8, 16, 4); // Middle
      g.fillRect(2, 12, 4, 4); // Left point
      g.fillRect(18, 12, 4, 4); // Right point
      g.fillRect(6, 16, 12, 4); // Bottom points
      g.fillRect(10, 18, 4, 4); // Bottom center
      
      // Star shine
      g.fillStyle(0xFFFEF7);
      g.fillRect(10, 4, 4, 2); // Center shine
      g.fillRect(12, 10, 2, 2); // Side shine
      
      // Star outline for depth
      g.fillStyle(0xDAA520);
      g.fillRect(8, 2, 2, 2); // Top left outline
      g.fillRect(14, 2, 2, 2); // Top right outline
    });
  }

  // === REALISTIC OBSTACLE SPRITES ===
  
  createRealisticObstacleSprites() {
    // Falling wedding cake (realistic)
    this.createPixelSprite('falling-cake', 16, 20, (g) => {
      // Cake layers with texture
      g.fillStyle(0xFFFEF7);
      g.fillRect(2, 12, 12, 8); // Bottom layer
      g.fillRect(5, 6, 6, 10); // Top layer
      
      // Cake texture
      g.fillStyle(0xF5F5DC); // Vanilla cake
      g.fillRect(3, 13, 10, 1);
      g.fillRect(3, 15, 10, 1);
      g.fillRect(6, 7, 4, 1);
      g.fillRect(6, 9, 4, 1);
      
      // Pink frosting (messy/falling)
      g.fillStyle(0xFF69B4);
      g.fillRect(1, 12, 14, 2); // Messy bottom frosting
      g.fillRect(4, 6, 8, 2); // Top frosting
      
      // Falling frosting drops
      g.fillStyle(0xFF69B4);
      g.fillRect(0, 4, 1, 2); // Left drop
      g.fillRect(15, 8, 1, 2); // Right drop
      g.fillRect(7, 2, 2, 2); // Center drop
      
      // Cake decorations falling off
      g.fillStyle(0xFFD700);
      g.fillRect(1, 5, 1, 1); // Flying decoration
      g.fillRect(14, 7, 1, 1); // Flying decoration
      
      // Motion lines
      g.fillStyle(0xC0C0C0);
      g.fillRect(0, 1, 1, 10); // Motion line 1
      g.fillRect(15, 3, 1, 8); // Motion line 2
    });

    // Rice shower (realistic)
    this.createPixelSprite('rice-shower', 20, 12, (g) => {
      // Individual rice grains
      g.fillStyle(0xFFFEF7);
      const ricePositions = [
        [1, 2], [3, 1], [5, 3], [7, 2], [9, 1], [11, 3],
        [13, 2], [15, 1], [17, 3], [19, 2], [2, 5], [4, 4],
        [6, 6], [8, 5], [10, 4], [12, 6], [14, 5], [16, 4], [18, 6]
      ];
      
      ricePositions.forEach(([x, y]) => {
        g.fillRect(x, y, 1, 2); // Individual rice grain
      });
      
      // Motion trails for rice
      g.fillStyle(0xE0E0E0);
      g.fillRect(2, 0, 1, 3); // Trail 1
      g.fillRect(8, 1, 1, 4); // Trail 2
      g.fillRect(14, 0, 1, 5); // Trail 3
      g.fillRect(18, 2, 1, 3); // Trail 4
    });

    // Champagne cork (realistic obstacle)
    this.createPixelSprite('champagne-cork', 8, 12, (g) => {
      // Cork body
      g.fillStyle(0xDEB887);
      g.fillRect(2, 4, 4, 6); // Cork body
      
      // Cork texture
      g.fillStyle(0xD2B48C);
      g.fillRect(2, 5, 4, 1); // Cork ring 1
      g.fillRect(2, 7, 4, 1); // Cork ring 2
      g.fillRect(2, 9, 4, 1); // Cork ring 3
      
      // Cork top (metallic cap)
      g.fillStyle(0xC0C0C0);
      g.fillRect(2, 2, 4, 2); // Metal cap
      g.fillStyle(0xFFD700); // Gold foil
      g.fillRect(2, 1, 4, 1); // Foil
      
      // Cork bottom
      g.fillStyle(0x8B4513);
      g.fillRect(3, 10, 2, 1); // Cork bottom
      
      // Champagne spray
      g.fillStyle(0xFFF8DC);
      g.fillRect(1, 0, 1, 2); // Spray left
      g.fillRect(3, 0, 2, 1); // Spray center
      g.fillRect(6, 0, 1, 2); // Spray right
    });

    // Flying bouquet (realistic obstacle)
    this.createPixelSprite('flying-bouquet', 12, 16, (g) => {
      // Bouquet stems
      g.fillStyle(0x228B22);
      g.fillRect(5, 8, 2, 8); // Main stems
      g.fillRect(4, 10, 1, 6); // Side stem
      g.fillRect(7, 9, 1, 7); // Side stem
      
      // Mixed flowers
      g.fillStyle(0xFF69B4); // Pink roses
      g.fillRect(3, 4, 2, 2);
      g.fillRect(7, 3, 2, 2);
      g.fillStyle(0xFFFFFF); // White flowers
      g.fillRect(5, 2, 2, 2);
      g.fillRect(2, 6, 2, 2);
      g.fillStyle(0xFFD700); // Yellow flowers
      g.fillRect(8, 5, 2, 2);
      g.fillRect(4, 7, 2, 2);
      
      // Ribbon flying
      g.fillStyle(0xFF1493);
      g.fillRect(4, 8, 4, 1); // Ribbon
      g.fillRect(1, 9, 2, 1); // Flying ribbon piece
      g.fillRect(9, 7, 2, 1); // Flying ribbon piece
      
      // Motion lines
      g.fillStyle(0xC0C0C0);
      g.fillRect(0, 1, 1, 6);
      g.fillRect(11, 3, 1, 4);
    });

    // Wedding guest (bumping into you)
    this.createPixelSprite('wedding-guest', 14, 20, (g) => {
      // Guest head
      g.fillStyle(0xFFDBB5);
      g.fillRect(5, 2, 4, 4); // Head
      
      // Hair
      g.fillStyle(0x654321);
      g.fillRect(4, 1, 6, 3); // Hair
      
      // Fancy dress/suit
      g.fillStyle(0x800080); // Purple formal wear
      g.fillRect(4, 6, 6, 10); // Dress/suit body
      g.fillRect(2, 8, 2, 6); // Left arm
      g.fillRect(10, 8, 2, 6); // Right arm
      
      // Dress details
      g.fillStyle(0xFF69B4); // Pink details
      g.fillRect(5, 7, 4, 1); // Neckline
      g.fillRect(4, 10, 6, 1); // Belt
      
      // Fancy hat
      g.fillStyle(0xFF69B4);
      g.fillRect(3, 0, 8, 2); // Large fancy hat
      g.fillStyle(0xFFD700); // Hat decoration
      g.fillRect(6, 0, 2, 1); // Hat ornament
      
      // Face (excited/surprised)
      g.fillStyle(0x000000);
      g.fillRect(6, 3, 1, 1); // Left eye
      g.fillRect(7, 3, 1, 1); // Right eye
      g.fillStyle(0xFF69B4); // Open mouth (surprised)
      g.fillRect(6, 4, 2, 1); // Mouth
      
      // Arms waving (excited gesture)
      g.fillStyle(0xFFDBB5); // Hands
      g.fillRect(1, 8, 1, 1); // Left hand
      g.fillRect(12, 8, 1, 1); // Right hand
    });

    // Spilled wine glass
    this.createPixelSprite('wine-spill', 10, 12, (g) => {
      // Broken glass
      g.fillStyle(0xF0F8FF);
      g.fillRect(3, 6, 4, 4); // Glass base
      g.fillRect(4, 4, 2, 2); // Glass bowl part
      
      // Wine spill
      g.fillStyle(0x722F37); // Dark red wine
      g.fillRect(2, 8, 6, 2); // Main spill
      g.fillRect(1, 9, 2, 1); // Spill extension
      g.fillRect(7, 9, 2, 1); // Spill extension
      g.fillRect(4, 10, 2, 1); // Drip
      
      // Glass shards
      g.fillStyle(0xE6E6FA);
      g.fillRect(1, 5, 1, 1); // Shard 1
      g.fillRect(8, 4, 1, 1); // Shard 2
      g.fillRect(2, 7, 1, 1); // Shard 3
      
      // Motion/impact lines
      g.fillStyle(0xC0C0C0);
      g.fillRect(0, 3, 1, 3);
      g.fillRect(9, 2, 1, 4);
    });
  }

  // === ITEM SPRITES (Enhanced) ===
  
  createItemSprites() {
    // Enhanced wedding ring - 20x20
    this.createPixelSprite('ring', 20, 20, (g) => {
      g.fillStyle(0xFFD700);
      g.fillRect(4, 4, 12, 12); // Ring outer
      g.fillStyle(0x000000);
      g.fillRect(8, 8, 4, 4); // Ring center
      g.fillStyle(0xFF1493); // Diamond
      g.fillRect(8, 2, 4, 4); // Diamond
      g.fillStyle(0xFFFFFF); // Diamond shine
      g.fillRect(8, 2, 2, 2); // Diamond highlight
      
      // Ring highlights
      g.fillStyle(0xFFF8DC);
      g.fillRect(4, 4, 2, 12); // Left highlight
      g.fillRect(6, 4, 8, 2); // Top highlight
    });

    // Enhanced heart coin - 20x20
    this.createPixelSprite('heart-coin', 20, 20, (g) => {
      // Gold coin base
      g.fillStyle(0xFFD700);
      g.fillRect(2, 2, 16, 16); // Coin outer
      
      // Heart shape
      g.fillStyle(0xFF1493);
      g.fillRect(4, 6, 4, 4); // Left heart
      g.fillRect(12, 6, 4, 4); // Right heart
      g.fillRect(6, 8, 8, 6); // Heart middle
      g.fillRect(8, 12, 4, 2); // Heart bottom
      
      // Coin highlights
      g.fillStyle(0xFFFEF7);
      g.fillRect(2, 2, 2, 16); // Left shine
      g.fillRect(4, 2, 12, 2); // Top shine
      
      // Coin edge
      g.fillStyle(0xDAA520);
      g.fillRect(16, 2, 2, 16); // Right edge
      g.fillRect(2, 16, 16, 2); // Bottom edge
    });

    // Enhanced bouquet - 20x32
    this.createPixelSprite('bouquet', 20, 32, (g) => {
      // Green stems with texture
      g.fillStyle(0x228B22);
      g.fillRect(8, 16, 4, 16); // Main stem
      g.fillStyle(0x32CD32); // Lighter green
      g.fillRect(8, 16, 2, 16); // Stem highlight
      
      // Mixed colorful flowers
      g.fillStyle(0xFF69B4); // Pink roses
      g.fillRect(4, 6, 4, 4);
      g.fillRect(12, 8, 4, 4);
      g.fillStyle(0xFFFFFF); // White flowers
      g.fillRect(8, 4, 4, 4);
      g.fillRect(2, 10, 4, 4);
      g.fillStyle(0xFFD700); // Yellow flowers
      g.fillRect(14, 4, 4, 4);
      g.fillRect(6, 12, 4, 4);
      
      // Flower centers
      g.fillStyle(0xFFD700);
      g.fillRect(6, 6, 2, 2); // Pink flower center
      g.fillRect(10, 6, 2, 2); // White flower center
      
      // Leaves
      g.fillStyle(0x2F5F2F);
      g.fillRect(2, 12, 2, 4); // Left leaf
      g.fillRect(16, 10, 2, 4); // Right leaf
      
      // Silk ribbon
      g.fillStyle(0xFF1493);
      g.fillRect(6, 14, 8, 4); // Main bow
      g.fillStyle(0xC71585); // Bow shadow
      g.fillRect(10, 16, 2, 2); // Bow center
    });

    // Enhanced champagne glass - 20x32
    this.createPixelSprite('champagne', 20, 32, (g) => {
      // Glass with realistic shape
      g.fillStyle(0xF0F8FF);
      g.fillRect(6, 6, 8, 16); // Glass bowl
      g.fillRect(8, 22, 4, 8); // Glass stem
      g.fillRect(4, 30, 12, 2); // Glass base
      
      // Champagne liquid
      g.fillStyle(0xFFF8DC);
      g.fillRect(8, 8, 4, 12); // Liquid
      
      // Bubbles rising
      g.fillStyle(0xFFFFFF);
      g.fillRect(8, 6, 2, 2); // Bubble 1
      g.fillRect(10, 8, 2, 2); // Bubble 2
      g.fillRect(8, 12, 2, 2); // Bubble 3
      g.fillRect(10, 16, 2, 2); // Bubble 4
      
      // Glass highlights
      g.fillStyle(0xFFFFFE);
      g.fillRect(6, 6, 2, 16); // Left highlight
      g.fillRect(8, 22, 2, 8); // Stem highlight
      
      // Glass rim
      g.fillStyle(0xE6E6FA);
      g.fillRect(6, 6, 8, 2); // Rim
    });

    // Enhanced wedding gift - 20x20
    this.createPixelSprite('gift', 20, 20, (g) => {
      // Gift box with texture
      g.fillStyle(0x4169E1);
      g.fillRect(4, 6, 12, 12); // Box base
      g.fillStyle(0x1E5AAA); // Box shading
      g.fillRect(14, 6, 2, 12); // Right shadow
      g.fillRect(4, 16, 12, 2); // Bottom shadow
      
      // Gift ribbons
      g.fillStyle(0xFF69B4);
      g.fillRect(8, 2, 4, 16); // Vertical ribbon
      g.fillRect(2, 10, 16, 4); // Horizontal ribbon
      
      // Ribbon highlights
      g.fillStyle(0xFFB6C1);
      g.fillRect(8, 2, 2, 16); // Vertical ribbon highlight
      g.fillRect(2, 10, 16, 2); // Horizontal ribbon highlight
      
      // Bow with depth
      g.fillStyle(0xFF1493);
      g.fillRect(6, 2, 8, 4); // Bow top
      g.fillStyle(0xC71585); // Bow shadow
      g.fillRect(8, 4, 4, 2); // Bow center
      
      // Gift tag
      g.fillStyle(0xFFFEF7);
      g.fillRect(16, 8, 4, 4); // Tag
      g.fillStyle(0x000000);
      g.fillRect(16, 8, 2, 2); // Tag text
    });

    // Enhanced cake slice - 20x20
    this.createPixelSprite('cake-slice', 20, 20, (g) => {
      // Cake base with layers
      g.fillStyle(0xFFFEF7);
      g.fillRect(4, 10, 12, 8); // Cake base
      g.fillStyle(0xF5F5DC); // Cake texture
      g.fillRect(4, 12, 12, 2); // Cake layer
      g.fillRect(4, 16, 12, 2); // Cake layer
      
      // Pink frosting layers
      g.fillStyle(0xFF69B4);
      g.fillRect(4, 10, 12, 2); // Top frosting
      g.fillRect(4, 6, 8, 4); // Side frosting
      
      // Frosting decoration
      g.fillStyle(0xFFB6C1);
      g.fillRect(6, 6, 4, 2); // Frosting swirl
      
      // Cherry on top
      g.fillStyle(0xDC143C);
      g.fillRect(8, 4, 4, 2); // Cherry
      g.fillStyle(0x228B22);
      g.fillRect(10, 2, 2, 2); // Cherry stem
      
      // Cake crumbs
      g.fillStyle(0xF5DEB3);
      g.fillRect(2, 16, 2, 2); // Crumb 1
      g.fillRect(16, 14, 2, 2); // Crumb 2
    });
  }

  // === UI SPRITES ===
  
  createUISprites() {
    // Enhanced UI Heart
    this.createPixelSprite('ui-heart', 10, 8, (g) => {
      g.fillStyle(0xFF1493);
      g.fillRect(2, 2, 2, 2); // Left heart
      g.fillRect(6, 2, 2, 2); // Right heart
      g.fillRect(3, 3, 4, 3); // Heart middle
      g.fillRect(4, 6, 2, 1); // Heart bottom
      
      // Heart highlights
      g.fillStyle(0xFF69B4);
      g.fillRect(2, 2, 1, 1); // Left shine
      g.fillRect(6, 2, 1, 1); // Right shine
      g.fillRect(4, 3, 2, 1); // Center shine
    });

    // Enhanced UI Coin
    this.createPixelSprite('ui-coin', 8, 8, (g) => {
      g.fillStyle(0xFFD700);
      g.fillRect(1, 1, 6, 6); // Coin outer
      g.fillStyle(0x000000);
      g.fillRect(3, 3, 2, 2); // Coin center
      
      // Coin shine
      g.fillStyle(0xFFFEF7);
      g.fillRect(1, 1, 1, 1); // Shine spot
      g.fillRect(2, 1, 4, 1); // Top shine
      
      // Coin edge
      g.fillStyle(0xDAA520);
      g.fillRect(6, 1, 1, 6); // Right edge
      g.fillRect(1, 6, 6, 1); // Bottom edge
    });
  }

  // === UTILITY FUNCTIONS ===
  
  // Get sprite with scaling
  getScaledSprite(key, scale = GAME_CONFIG.SPRITES.SCALE) {
    const sprite = this.scene.add.sprite(0, 0, key);
    sprite.setScale(scale);
    return sprite;
  }

  // Create particle effect
  createParticle(x, y, type, color = 0xFFFFFF) {
    const particle = this.scene.add.graphics();
    particle.fillStyle(color);
    
    switch (type) {
      case 'heart':
        particle.fillRect(0, 1, 1, 1);
        particle.fillRect(2, 1, 1, 1);
        particle.fillRect(0, 2, 3, 2);
        particle.fillRect(1, 4, 1, 1);
        break;
        
      case 'star':
        particle.fillRect(1, 0, 1, 1);
        particle.fillRect(0, 1, 3, 1);
        particle.fillRect(1, 2, 1, 1);
        break;
        
      case 'sparkle':
        particle.fillRect(1, 0, 1, 3);
        particle.fillRect(0, 1, 3, 1);
        break;
        
      default: // dot
        particle.fillRect(0, 0, 2, 2);
    }
    
    particle.setPosition(x, y);
    particle.setScale(2);
    
    return particle;
  }
}

export default SpriteFactory;